import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { NeonLoadingRing } from '@/components/feedback/NeonLoadingRing';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { resolveEntitlement } from '@/utils/entitlementResolver';
import { hasActiveJourneyPurchase } from '@/utils/journeyAccess';
import { getUpgradePath } from '@/utils/upgradeRoute';
import { AccessRetryState } from '@/components/routing/AccessRetryState';
import { ProgramPreviewBoundary } from '@/components/journeys/course/ProgramPreviewBoundary';
import { CourseWritingPreviewBoundary } from '@/components/journeys/course/CoursePreviewContext';
import { isGuestAccessibleProgram, isSharedCreatorProgram } from '@/utils/creatorProgramAccess';
import { useCoreAccess, type CoreAccessStatus } from '@/hooks/useCoreAccess';
import { fetchProgramAccessAnswer } from '@/utils/programAccess';
import { fetchCanStartWithoutAccount } from '@/utils/startGate';

interface JourneySessionAccessRouteProps {
  children: ReactNode;
}

function isUuid(value: string | null | undefined) {
  return Boolean(
    value &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  );
}

async function userCanAccessJourneyProgram(input: {
  userId: string;
  journeyRef: string;
  programId: string;
  canPreviewDraftJourney: boolean;
}) {
  const journeyQuery = supabase.from('journeys').select('id, is_published, created_by, creation_source, experience_config').limit(1);
  const { data: journey, error: journeyError } = isUuid(input.journeyRef)
    ? await journeyQuery.eq('id', input.journeyRef).maybeSingle()
    : await journeyQuery.eq('slug', input.journeyRef).maybeSingle();

  if (journeyError || !journey?.id) return false;
  const ownerPreview = journey.creation_source === 'creator' && journey.created_by === input.userId;
  if (!journey.is_published && !input.canPreviewDraftJourney && !ownerPreview) return false;

  const { data: journeyUnit, error: unitError } = await supabase
    .from('journey_days')
    .select('id')
    .eq('journey_id', journey.id)
    .eq('program_id', input.programId)
    .eq('unit_type', 'powerprompt')
    .limit(1)
    .maybeSingle();

  if (unitError || !journeyUnit?.id) return false;

  if (input.canPreviewDraftJourney || (!journey.is_published && ownerPreview)) return true;

  return hasActiveJourneyPurchase(input.userId, journey.id);
}

/**
 * 'retry': the answer depends on core access and the server could not give
 * it, so the route shows a retry state instead of the session or the offer.
 */
type StandaloneProgramAccess = 'allow' | 'deny' | 'retry';

/** The server's program access answer; an error is 'retry', never a no. */
async function serverProgramAccess(
  userId: string,
  programId: string,
  whenDenied: StandaloneProgramAccess,
): Promise<StandaloneProgramAccess> {
  try {
    return (await fetchProgramAccessAnswer(userId, programId)) ? 'allow' : whenDenied;
  } catch {
    return 'retry';
  }
}

async function canAccessStandaloneProgram(
  programRef: string,
  userId?: string,
  coreAccessStatus?: CoreAccessStatus
): Promise<StandaloneProgramAccess> {
  const query = supabase
    .from('programs')
    .select('id, access_tier, is_published, visibility, program_scope, creation_source, created_by, program_type, deleted_at')
    .limit(1);

  const { data, error } = isUuid(programRef)
    ? await query.eq('id', programRef).maybeSingle()
    : await query.eq('slug', programRef).maybeSingle();

  // A failed lookup is unknown, not a no: ask the member to try again.
  if (error) return 'retry';
  if (!data) return 'deny';

  if (isGuestAccessibleProgram(data)) {
    // Once the core access gate is active, an official free program is not
    // open to a member without core access: the server decides (a /free
    // sample, a grant). Creator share programs stay open.
    if (userId && coreAccessStatus === 'none' && !isSharedCreatorProgram(data)) {
      return serverProgramAccess(userId, data.id, 'deny');
    }
    // Core access failed to load: only a server yes (a /free sample, a grant)
    // opens the program; anything else asks the member to try again.
    if (userId && coreAccessStatus === 'error' && !isSharedCreatorProgram(data)) {
      return serverProgramAccess(userId, data.id, 'retry');
    }
    // Signed out: the server's Start rule (samples and creator programs; any
    // free program before release day). A missing function keeps today's yes.
    if (!userId && !isSharedCreatorProgram(data)) {
      return (await fetchCanStartWithoutAccount(data.id, true)) ? 'allow' : 'deny';
    }
    return 'allow';
  }
  if (data.deleted_at) return 'deny';

  // Anyone who may create programs can test their own work, without gaining
  // consumer access to anyone else's Premium catalog programs. is_pro is a
  // billing fact, so it is not the check here.
  if (!userId || data.created_by !== userId) return 'deny';
  const { data: creatorAccess, error: accessError } = await supabase.rpc('get_my_creator_access');
  if (accessError) return 'deny';
  const access = creatorAccess as { can_create_powerprompt?: boolean; is_admin?: boolean } | null;
  return access?.can_create_powerprompt === true || access?.is_admin === true ? 'allow' : 'deny';
}

function JourneySessionAccessRouteInner({ children }: JourneySessionAccessRouteProps) {
  const { user, profile, loading: authLoading } = useAuth();
  const { programId } = useParams<{ programId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hasPremiumAccess, isLoadingSubscription } = useSubscription();
  const journeyRef = searchParams.get('journeyId');
  const shouldCheckJourneyAccess = Boolean(user?.id && journeyRef && programId);
  const canPreviewDraftJourney = Boolean(
    profile?.is_admin ||
      profile?.roles?.some((role) => ['admin', 'manager', 'superadmin'].includes(role))
  );

  const { data: hasJourneySessionAccess = false, isLoading: isLoadingJourneyAccess } = useQuery({
    queryKey: ['journeySessionAccess', user?.id, journeyRef, programId, canPreviewDraftJourney],
    queryFn: () =>
      userCanAccessJourneyProgram({
        userId: user!.id,
        journeyRef: journeyRef!,
        programId: programId!,
        canPreviewDraftJourney,
      }),
    enabled: shouldCheckJourneyAccess,
    staleTime: 60 * 1000,
    retry: false,
  });

  const coreAccess = useCoreAccess();
  // A signed-in member's free access depends on core access: wait for it.
  const isLoadingCoreAccess = Boolean(user?.id) && coreAccess.status === 'loading';
  const shouldCheckFreeProgramAccess = Boolean(programId && !journeyRef && !hasPremiumAccess && !isLoadingCoreAccess);

  const {
    data: freeProgramAccess,
    isLoading: isLoadingFreeProgramAccess,
    refetch: refetchFreeProgramAccess,
  } = useQuery({
    queryKey: ['sessionFreeProgramAccess', user?.id, programId, coreAccess.status],
    queryFn: () => canAccessStandaloneProgram(programId!, user?.id, coreAccess.status),
    enabled: shouldCheckFreeProgramAccess,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const hasFreeProgramAccess = freeProgramAccess === 'allow';
  const needsCoreAccessRetry = freeProgramAccess === 'retry';

  const accessDecision = resolveEntitlement({
    proofs: journeyRef
      ? [{ source: 'program_access', hasAccess: hasJourneySessionAccess }]
      : [
          { source: 'free', hasAccess: hasFreeProgramAccess },
          { source: 'membership', hasAccess: hasPremiumAccess },
        ],
    loadingChecks: [
      { isLoading: authLoading, mode: 'always' },
      { isLoading: isLoadingJourneyAccess, mode: 'always' },
      {
        isLoading: shouldCheckFreeProgramAccess && isLoadingFreeProgramAccess,
        mode: 'until-access',
      },
      {
        isLoading: Boolean(user?.id) && !journeyRef && isLoadingSubscription,
        mode: 'until-access',
      },
      {
        isLoading: !journeyRef && Boolean(programId) && isLoadingCoreAccess,
        mode: 'until-access',
      },
    ],
  });

  if (accessDecision.shouldBlockRender) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-v7-bg-primary">
        <div className="text-center" role="status" aria-live="polite" aria-atomic="true">
          <NeonLoadingRing className="mx-auto h-12 w-12" />
          <p className="mt-2 text-v7-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user?.id) {
    if (journeyRef || !hasFreeProgramAccess) {
      const redirectPath = `${location.pathname}${location.search}${location.hash}`;
      return <Navigate to={`/auth?mode=signup&redirect=${encodeURIComponent(redirectPath)}`} replace />;
    }

    return <>{children}</>;
  }

  if (!accessDecision.hasAccess && needsCoreAccessRetry) {
    // Never the session, never the offer: the member's core access is unknown.
    return (
      <AccessRetryState
        onRetry={() => {
          if (coreAccess.status === 'error') void coreAccess.refetch();
          void refetchFreeProgramAccess();
        }}
      />
    );
  }

  if (!accessDecision.hasAccess) {
    // A member-tier program keeps the visitor in context: the program detail
    // page opens its upgrade modal instead of stranding them on /pricing.
    if (!journeyRef && programId) {
      return <Navigate to={`/program/${programId}?paywall=1`} replace />;
    }
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={getUpgradePath(redirectPath)} replace />;
  }

  return <>{children}</>;
}

export default function JourneySessionAccessRoute({ children }: JourneySessionAccessRouteProps) {
  const [params] = useSearchParams();
  if (params.has('preview') || params.has('previewSnapshot') || params.has('previewProgram')) {
    if (params.has('previewProgram')) return <ProgramPreviewBoundary>{children}</ProgramPreviewBoundary>;
    return <CourseWritingPreviewBoundary surface="powerprompt">{children}</CourseWritingPreviewBoundary>;
  }
  return <JourneySessionAccessRouteInner>{children}</JourneySessionAccessRouteInner>;
}
